import styles from './RelationsTable.module.scss';

import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCtrlMouseClickLink } from '@work4all/components/lib/hooks/use-ctrl-mouse-click-link';

import { nameof } from '@work4all/data/lib/helper/nameof';
import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';

import { AddressConnection } from '@work4all/models/lib/Classes/AddressConnection.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';
import { ITableSchema } from '@work4all/models/lib/table-schema/table-schema';

import { IEntityTable } from '../../../../../../../../../../components/data-tables/entity-table/EntityTable';
import { IEntityTableV8 } from '../../../../../../../../../../components/data-tables/entity-table/EntityTableV8';
import { EntityTableLayoutV8 } from '../../../../../../../../../../components/data-tables/table/EntityTableLayoutV8';
import { useDataTableV8 } from '../../../../../../../../../../components/data-tables/use-data-table-v8';
import { useDeleteEntitiesToolbarConfig } from '../../../../../../../../../../components/data-tables/use-delete-entities-toolbar-config';
import { RelationsPopover } from '../../../../../../../../../../components/relations-popover';
import { RelationsPopoverProps } from '../../../../../../../../../../components/relations-popover/interfaces';
import { useMaskContext } from '../../../../../../../hooks/mask-context';
import { BusinessPartners } from '../../../../../../businessPartners/BusinessPartnerOverlayController';

type IRelationsTableCustomColumns = Record<string, never>;
type IRelationsTableSchema = ITableSchema<IRelationsTableCustomColumns>;
type IRelationsTableProps = Partial<IEntityTable> &
  Pick<IEntityTableV8, 'enableURLState'>;

const defaultSort = [{ field: 'id', direction: SortDirection.ASCENDING }];

export const RelationsTable = (props: IRelationsTableProps) => {
  const anchorRef = useRef(null);
  const { activeTenant } = useTenant();
  const { data: entityData, entity } = useMaskContext<BusinessPartners>();

  const { t } = useTranslation();

  const schema: IRelationsTableSchema = useMemo(
    () => ({
      entity: Entities.addressConnection,
      columns: [
        {
          title: t('COMMON.RELATION_TYPE'),
          accessor: 'connection.name',
          sortable: false,
          groupable: true,
          filterable: { type: 'Search' },
          width: 200,
        },
        {
          title: 'COMMON.BUSINESSPARTNER',
          accessor: 'businessPartnerB.data.name',
          sortable: false,
          groupable: false,
          cell: 'BusinessPartnerCell',
          filterable: false,
          width: 440,
        },
      ],
      rowModifiers: [],
    }),
    [t]
  );

  const prefilter = useMemo(() => {
    return [
      { [nameof<AddressConnection>('sdObjMemberIdA')]: { $eq: entityData.id } },
    ];
  }, [entityData?.id]);

  const dataTable = useDataTableV8<AddressConnection, any>({
    ...props,
    schema: schema as IRelationsTableSchema,
    layout: 'table',
    prefilter,
    defaultSort,
    cellProcessors: {
      BusinessPartnerCell: (cfg) => {
        return [cfg.accessor, 'businessPartnerB.data.name'];
      },
    },
    cells: {
      BusinessPartnerCell: (cfg) => {
        const data: AddressConnection = cfg.row?.original;
        return (
          <div>
            {data.businessPartnerA?.data.name ||
              data.businessPartnerB?.data.name}
          </div>
        );
      },
    },
  });

  const { getLink } = useCtrlMouseClickLink();

  const selectedFlatRows = dataTable.table.getSelectedRowModel().flatRows;

  const deleteConfig = useDeleteEntitiesToolbarConfig({
    entityType: Entities.addressConnection,
    selectedEntities: dataTable.selectedEntities,
  });
  const openInNewTab = useMemo(
    () => ({
      getPath: (relationId?: string) => {
        const relation = dataTable.data.find(
          (d) => d.id === Number(relationId)
        );
        const businessPartner = relation.businessPartnerB?.data;
        let path = '';
        if (!businessPartner?.id || !businessPartner?.__typename) {
          const id = // @ts-expect-error TECH_DEBT
            selectedFlatRows.filter((r) => !r.getIsGrouped())[0].original.id;
          path = getLink({ entity, id });
          return null;
        }
        const businessPartnerType =
          businessPartner.__typename === 'Kunde'
            ? Entities.customer
            : Entities.supplier;
        path = `/t-${activeTenant}/${businessPartnerType}s/${businessPartner.id}`;
        return path;
      },
    }),
    [activeTenant, dataTable.data, entity, getLink, selectedFlatRows]
  );

  const [relationsPopover, setRelationsPopover] = useState<
    Pick<RelationsPopoverProps, 'open' | 'isCreateMode'>
  >({ open: false, isCreateMode: false });

  return (
    <div ref={anchorRef} className={styles.root}>
      <RelationsPopover
        fullScreenMode
        onClose={() => {
          setRelationsPopover({ open: false, isCreateMode: false });
        }}
        businessPartnerId={entityData.id}
        businessPartnerType={
          entityData.__typename === 'Kunde'
            ? SdObjType.KUNDE
            : SdObjType.LIEFERANT
        }
        anchorEl={anchorRef.current}
        relationId={dataTable.selectedEntity?.id}
        {...relationsPopover}
      />

      <EntityTableLayoutV8
        {...props}
        {...dataTable}
        actions={{
          add: {
            handler: () => {
              setRelationsPopover({
                open: true,
                isCreateMode: true,
              });
            },
            disabled: false,
          },
          edit: {
            disabled: false,
            handler: () => {
              setRelationsPopover({
                open: true,
                isCreateMode: false,
              });
            },
          },
          openInNewTab,
          remove: deleteConfig,
        }}
        basicClasses={{ headerWrapper: styles.table }}
      />
    </div>
  );
};
